import {Routes, Route, Navigate, useNavigate, useLocation} from 'react-router-dom'
// 5 优化 R-B: NotificationBell + OnboardingModal 改 lazy,首屏不下载其 chunk
const OnboardingModal = lazy(() => import(/* webpackChunkName: "onboarding-modal" */ './components/OnboardingModal'))
import { Layout, Menu, Button, Dropdown, Tooltip, App as AntApp } from 'antd'
import {FileTextOutlined, ProjectOutlined, DashboardOutlined, ClockCircleOutlined, SafetyOutlined, DollarOutlined, InboxOutlined, AimOutlined, MenuFoldOutlined, FileProtectOutlined, FileSyncOutlined, SettingOutlined, CloudOutlined, HighlightOutlined, ExpandOutlined, CompressOutlined, SearchOutlined, EnvironmentOutlined, MessageOutlined, WarningOutlined, FundOutlined, DesktopOutlined, PictureOutlined} from '@ant-design/icons'
import { useAuth, AuthProvider, ProjectsProvider, useProjects, useProjectsFetched, setLastVisited, getLastVisited } from './hooks/useAuth'
import { useDraggableModal } from './hooks/useDraggableModal'
import useShortcuts from './hooks/useShortcuts'
import usePermissions from './hooks/usePermissions'
import {useQueryClient} from '@tanstack/react-query'
import { useApiQuery } from './hooks/useApiQuery'
import { useMessage } from './hooks/useMessage'
import { qk } from './constants/queryKeys'
import { safeInt } from './utils'
import { persistCurrentProject } from './utils/currentProject'
// import axios from 'axios'
import { useState, useEffect, Suspense, lazy, useCallback, useMemo, useRef } from 'react'
import ErrorBoundary from './components/ErrorBoundary'
import ProjectPermissionBanner from './components/ProjectPermissionBanner'
import NetworkStatusBanner from './components/common/NetworkStatusBanner'  // R-feat-2026-10-17 U4:断网 banner + 离线队列
// R-fix-2026-08-02: 注入构建版本(commit + 时间),footer 显示让用户能验证
// 自己加载的 bundle 是哪个 commit。public/build-info.json 由 scripts/inject_build_info.sh
// 在 build 前重写,vite build 把 public/* 原样拷到 dist 根。运行时 fetch 而非 import
// (JSON import 在 vite 公开文件场景需要 ?raw 后缀,改成 fetch 更简单)。
function useBuildInfo() {
  const [info, setInfo] = useState({ commit: 'unknown', build_time: '', branch: '' })
  useEffect(() => {
    fetch('/build-info.json')
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setInfo(d))
      .catch(() => { /* 取不到就显示 unknown */ })
  }, [])
  return info
}
const NotificationBell = lazy(() => import(/* webpackChunkName: "notification-bell" */ './components/NotificationBell'))
// R260: 全局搜索 Ctrl+K 才打开,首屏不必加载 — lazy 让 GlobalSearchModal 走独立 chunk
const GlobalSearchModal = lazy(() => import('./components/GlobalSearch/GlobalSearchModal'))
// 2026-10-17 易用性 U3: ? 键打开的快捷键面板 — lazy 加载
const ShortcutsModal = lazy(() => import('./components/common/ShortcutsModal'))
import RequireModule from './components/RequireModule'
import RequireAuth from './components/RequireAuth'
import ProjectGuard from './components/RouteGuard/ProjectGuard'
import PlatformOwnerOnly from './components/RouteGuard/PlatformOwnerOnly'  // R-audit-2026-08-30 P0-001: 平台所有者路由守卫
import ProjectFavoriteSelect from './components/ProjectFavorite/ProjectFavoriteSelect'  // R-feat-2026-10-14 #5 易用性:项目收藏选择器

// R-fix-2026-08-17: 监听全局项目 404 事件,跳回项目列表。
// 后端任意 /api/projects/{id} 返 404 时(典型场景:数据库清空后浏览器
// 缓存了 stale project_id),自动 Navigate('/projects') 避免 100+ 个 404。
function ProjectNotFoundListener() {
  const navigate = useNavigate()
  const message = useMessage()
  useEffect(() => {
    const handler = (e) => {
      const pid = e.detail?.projectId
      message.warning(`项目 ID ${pid} 不存在或已被删除,已返回项目列表`)
      navigate('/projects', { replace: true })
    }
    window.addEventListener('pmwork:project-404', handler)
    return () => window.removeEventListener('pmwork:project-404', handler)
  }, [navigate, message])
  return null
}

// R260: 账户信息弹窗仅在用户点头部菜单时打开,首屏不加载 — lazy
const UserInfoModal = lazy(() => import('./components/UserInfoModal'))

const Login = lazy(() => import('./pages/Login'))
const ShareReportPage = lazy(() => import('./pages/ShareReportPage'))
const ProjectList = lazy(() => import('./pages/ProjectList'))
const ProjectsPortfolio = lazy(() => import('./pages/projects/Portfolio'))  // R-audit-2026-09-20:项目组合放大独立路由(无侧栏)
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Schedule = lazy(() => import('./pages/Schedule'))
const SafetyQuality = lazy(() => import('./pages/SafetyQuality'))
const Documents = lazy(() => import('./pages/Documents'))
// R-fix-2026-09-05: 通用文件预览(viewer),支持 documents / regulations 等 type
const FilePreview = lazy(() => import('./pages/FilePreview'))
const AIAssistant = lazy(() => import('./pages/AIAssistant'))
const TestData = lazy(() => import('./pages/TestData'))
const Contract = lazy(() => import('./pages/Contract'))
const Investment = lazy(() => import('./pages/Investment'))             // R-2026-08-28: 跨项目投资总览
const InvestmentDetail = lazy(() => import('./pages/InvestmentDetail')) // R-2026-08-28: 项目内投资管理 6 tab
const Material = lazy(() => import('./pages/Material'))
const ProjectMap = lazy(() => import('./pages/ProjectMap'))
const Procedures = lazy(() => import('./pages/Procedures'))
const SharedData = lazy(() => import('./pages/SharedData'))
const Meeting = lazy(() => import('./pages/Meeting'))
const Design = lazy(() => import('./pages/Design'))
const Admin = lazy(() => import('./pages/Admin'))
const PlatformAdmin = lazy(() => import('./pages/PlatformAdmin'))
// 2026-09-01: MyApprovals 组件随审批功能移除;保留 /approvals → /workbench 兜底重定向(旧书签)
// 2026-08-31 个人工作台
const MyWorkbench = lazy(() => import('./pages/MyWorkbench'))
// 2026-08-31 日历视图
const Calendar = lazy(() => import('./pages/Calendar'))
// 2026-09-01 个人工作台 P1.5
const WorkLogTemplates = lazy(() => import('./pages/WorkLogTemplates'))
const MyApprovals = lazy(() => import('./pages/approvals'))
const ApprovalInstanceDetail = lazy(() => import('./pages/approvals/instance-detail'))
// R-2026-09-19: 项目组合已并入 InvestmentDetail tab,App.jsx 不再需要 lazy Portfolio
const OpenApiDocs = lazy(() => import('./pages/admin/OpenApiDocs'))
const Workload = lazy(() => import('./pages/workload'))
const Timesheet = lazy(() => import('./pages/timesheet'))
const ResourcePool = lazy(() => import('./pages/resource-pool'))
const TwoFactorSettings = lazy(() => import('./pages/security/TwoFactorSettings'))
const SCurve = lazy(() => import('./pages/s-curve'))
const DashboardCustomize = lazy(() => import('./pages/dashboard/DashboardCustomize'))
const MobileTabBar = lazy(() => import('./components/MobileTabBar'))
// 2026-10-13: 删 3 个无 caller 死文件 OnPremConfig / HealthDashboard / AuditLog + AlertsCard 组件 + 死文件相关注释清理
// 之前保留是等 route 接入,2026-10-04 audit 仍未挂载 → eslint no-unused-vars 持续报 3 个 warning
// + 占 3 个 lazy chunk 名(实际无路由触发,体积白下载)。需要时按 ReportTemplateEditor 模式重新挂载。
const ReportTemplateEditor = lazy(() => import('./pages/admin/ReportTemplateEditor'))
// 2026-09-01 个人工作台 P2.2
const WorkLogs = lazy(() => import('./pages/WorkLogs'))
// 2026-09-01 报表系统重构 P6.3
const Reports = lazy(() => import('./pages/Reports'))

// M1 P0
const Svo = lazy(() => import('./pages/Svo'))
const SiteContact = lazy(() => import('./pages/SiteContact'))
const SealUsage = lazy(() => import('./pages/SealUsage'))
const DocumentHandover = lazy(() => import('./pages/DocumentHandover'))
const WorkHandover = lazy(() => import('./pages/WorkHandover'))
const WorkPermit = lazy(() => import('./pages/WorkPermit'))
const Qs = lazy(() => import('./pages/Qs'))
const Mr = lazy(() => import('./pages/Mr'))
const RiskPage = lazy(() => import('./pages/Risk'))
// M1 P1
const Concealed = lazy(() => import('./pages/Concealed'))
const Energization = lazy(() => import('./pages/Energization'))
// M1 P2
const Photo = lazy(() => import('./pages/Photo'))
const Regulations = lazy(() => import('./pages/Regulations'))  // 制度规范库(无侧栏入口,相关页面按钮进入)
const DocumentCheck = lazy(() => import('./pages/DocumentCheck'))  // 资料检查 v2(无侧栏入口,Documents.jsx 入口卡进入)
const DocumentCheckCriteria = lazy(() => import('./pages/DocumentCheckCriteria'))  // AI 检查依据管理(从 AI 检查 modal 进入)
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))

// 现场管理 (5 个子模块 Tab 容器)
const SiteManagement = lazy(() => import('./pages/SiteManagement'))

// 项目健康度仪表盘 (R2)
const ProjectHealth = lazy(() => import('./pages/ProjectHealth'))

// 旧路由 SafetyRedirect / QualityRedirect / CoRedirect 已删除(2026-08-31 全面审计 P3.1):
//  /safety/:id /quality/:id /co/:id 是历史单模块路由残留,业务已统一到 /safety-quality/:id 和 /contract/:id
//  保留 /safety-quality/ 和 /contract/ 末尾斜杠重定向到 /projects(保留兼容外部链接)

const { Header, Content } = Layout

import { useIsMobile } from './hooks/useIsMobile'  // F2-12 移动端 H5 自适应
import { useUserLocalStorage } from './hooks/useUserLocalStorage'
import { STALE_TIME } from './constants/staleTime.js'

// ============ 菜单配置 ============
// module 字段用于权限过滤(空表示无需权限)
// module 值必须镜像后端 backend/constants.py 的 MODULE_NAMES keys
const MENU_ITEMS = [
  // R-audit-2026-09-03: 我的工作台独立 module key 'my-workbench',
  //   不再复用 dashboard(原注释"复用 dashboard 是已核实的安全做法"实际造成
  //   没 dashboard 权限的用户看不到工作台入口;有 dashboard 权限的不一定需要工作台)
  //   三镜像:App.jsx / backend constants.py MODULE_NAMES / 后端 permissions CSV
  //   同步。后端 constants.py 已加 my-workbench 条目;此处改 module 值即可。
  // 排第一 = 登录后 DefaultLandingRedirect 落到 /workbench(配合 NO_PROJECT_KEYS)。
  { key: '/workbench', label: '我的工作', icon: <DesktopOutlined />, module: 'my-workbench' },
  // 2026-08-31 日历已集成到工作台"日历"分区(产品差距分析第 4 项 第 2 步),侧栏不重复入口
  // 完整日历页保留 /calendar 路由,工作台 SectionCard「完整日历 →」按钮跳转
  { key: '/projects', label: '项目总览', icon: <ProjectOutlined />, module: 'projects' },
  { key: '/dashboard', label: '数据看板', icon: <DashboardOutlined />, module: 'dashboard' },
  // R-2026-08-28 v3: 用户决策 - "投资管理" → "投资控制"(全过程成本控制 + 项目阶段)
  { key: '/investment', label: '投资控制', icon: <FundOutlined />, module: 'investment' },
  // R-2026-09-19: 项目组合已合并到投资控制 tab,侧栏入口去掉
  { key: '/procedures', label: '项目手续', icon: <FileProtectOutlined />, module: 'procedures' },
  { key: '/design', label: '设计管理', icon: <HighlightOutlined />, module: 'design' },
  { key: '/schedule', label: '进度管理', icon: <ClockCircleOutlined />, module: 'schedule' },
  { key: '/safety-quality', label: '安全质量', icon: <SafetyOutlined />, module: 'safety_quality' },
  { key: '/contract', label: '合同执行', icon: <DollarOutlined />, module: 'contract' },
  { key: '/material', label: '物资管理', icon: <InboxOutlined />, module: 'material' },
  { type: 'divider' },
  { key: '/site-management', label: '现场管理', icon: <EnvironmentOutlined />, module: 'svo' },
  // 2026-09-26: 进度照片从 SiteManagement tab 抽为独立导航项(divider 内,紧跟现场管理)
  { key: '/photo', label: '项目照片', icon: <PictureOutlined />, module: 'photo' },
  { key: '/documents', label: '资料管理', icon: <FileTextOutlined />, module: 'documents' },
  { key: '/meeting', label: '会议记录', icon: <FileSyncOutlined />, module: 'meeting' },
  { key: '/project-map', label: '项目地图', icon: <AimOutlined />, module: 'project-map' },
  { key: '/ai', label: 'AI小助理', icon: <MessageOutlined />, module: 'ai' },
  { key: '/admin', label: '系统管理', icon: <SettingOutlined />, module: 'admin' },
]

function getSelectedKey(pathname) {
  if (pathname === '/workbench') return '/workbench'
  // 2026-09-19 F1-G04 恢复: /approvals 顶级路由
  if (pathname === '/approvals' || pathname.startsWith('/approvals/')) return '/approvals'
  // 2026-09-19 F1-G10 Portfolio → 已合并到投资控制 tab
  if (pathname === '/portfolio') return '/investment'
  // 2026-09-19 F2-08 OpenAPI 文档
  if (pathname === '/admin/api-docs' || pathname.startsWith('/admin/api-docs')) return '/admin'
  // M4-A: 工作负载
  if (pathname === '/workload' || pathname.startsWith('/workload')) return '/workload'
  // M4-B: 工时填报
  if (pathname === '/timesheet' || pathname.startsWith('/timesheet')) return '/timesheet'
  // M4-C: 资源池
  if (pathname === '/resource-pool' || pathname.startsWith('/resource-pool')) return '/resource-pool'
  // M4-D: 2FA 设置
  if (pathname === '/security/2fa' || pathname.startsWith('/security/')) return '/me'
  // M5-B: S-Curve
  if (pathname === '/s-curve' || pathname.startsWith('/s-curve')) return '/investment'
  // M5-C: 自定义仪表盘
  if (pathname === '/dashboard-customize' || pathname.startsWith('/dashboard-customize')) return '/workbench'
  // M6-B: 健康看板
  if (pathname === '/admin/health-dashboard' || pathname.startsWith('/admin/health-dashboard')) return '/admin'
  // M6-D: On-Prem 私有化
  if (pathname === '/admin/onprem' || pathname.startsWith('/admin/onprem')) return '/admin'
  // M7-B: 审计日志
  if (pathname === '/admin/audit-log' || pathname.startsWith('/admin/audit-log')) return '/admin'
  if (pathname.includes('/site-management')) return '/site-management'
  if (pathname === '/dashboard' || pathname.startsWith('/dashboard/')) return '/dashboard'
  if (pathname.includes('/schedule')) return '/schedule'
  if (pathname.includes('/safety') || pathname.includes('/quality')) return '/safety-quality'
  if (pathname.includes('/documents')) return '/documents'
  // 2026-09-04: 制度规范库是从资料管理点进来的(无独立菜单),高亮来源页
  if (pathname.includes('/regulations')) return '/documents'
  // 2026-09-04 v6: 资料检查(无独立菜单,从 Documents.jsx 入口卡进入,高亮来源页 documents)
  if (pathname.includes('/document-check')) return '/documents'
  if (pathname.includes('/contract')) return '/contract'
  if (pathname === '/investment' || pathname.startsWith('/investment/')) return '/investment'
  if (pathname.includes('/svo')) return '/svo'
  if (pathname.includes('/qs')) return '/qs'
  if (pathname.includes('/mr')) return '/mr'
  if (pathname.includes('/risk')) return '/risk'
  if (pathname.includes('/material')) return '/material'
  if (pathname.includes('/procedures')) return '/procedures'
  if (pathname.includes('/project-map')) return '/project-map'
  if (pathname.includes('/shared-data')) return '/shared-data'
  if (pathname.includes('/meeting')) return '/meeting'
  if (pathname.includes('/concealed')) return '/concealed'
  if (pathname.includes('/energization')) return '/energization'
  if (pathname.includes('/photo')) return '/photo'
  if (pathname.includes('/design')) return '/design'
  if (pathname === '/test') return '/test'
  if (pathname.includes('/ai')) return '/ai'
  if (pathname === '/admin') return '/admin'
  if (pathname.includes('/platform-admin')) return '/platform-admin'
  // 2026-09-01 OPT-A3: 删 '/approvals' 顶级 fallback(路由已删,fallback 也不需要)
  if (pathname === '/approval-designer') return '/admin'
  return '/projects'
}

// ============ 默认落地页重定向 ============
// R-fix-2026-08-19: 登录后默认页改为「首个有权限的菜单项」。
// 之前硬编码 / → /dashboard → /projects,导致没有 projects 权限的账号(例如 xieshiye
// 只有 safety 权限)登录后会落到 /projects 页面,侧栏虽然隐藏该项,但 URL 和内容仍指
// 到无权限模块,与「登录后只能看到自己有权限页面」的预期不符。
// 行为:
// 1. 等 usePermissions 加载完 — 否则首帧基于空 modules[] 误判会把所有用户都路由到 /projects
// 2. 按 MENU_ITEMS 顺序遍历,找第一个 canViewModule(item.module) === true 的项
// 3. 对需要 projectId 的菜单项,等 isProjectsFetched 后用 currentProjectId || projects[0]?.id 拼路径
// 4. 全部无权限时退到 /projects(由 RequireAnyModule 接管显示「暂无可用模块」)
const NO_PROJECT_KEYS = ['/projects', '/ai', '/test', '/shared-data', '/admin', '/platform-admin', '/workbench']  // 2026-09-01 OPT-A3: 删 '/approvals'

// 2026-10-17 易用性 U3: 全局快捷键清单(给 ShortcutsModal 显示)
// 与下面 useShortcuts 注册的 handlers 镜像;若修改快捷键,务必同步更新此处。
const GLOBAL_SHORTCUTS = [
  { key: 'k', require: 'cmd', description: '打开全局搜索', category: '搜索' },
  { key: '?', description: '显示快捷键面板', category: '帮助' },
  { key: 'g', description: '组合前缀(g 后 1s 内按字母)', category: '导航' },
  { key: 's', description: '跳转进度管理(g s)', category: '导航' },
  { key: 'd', description: '跳转数据看板(g d)', category: '导航' },
  { key: 'w', description: '跳转我的工作(g w)', category: '导航' },
  { key: 'Escape', description: '关闭当前弹窗', category: '弹窗' },
]

// R-audit-2026-08-26: 平台所有者权限不一致时显示的红条横幅。
// 检测条件:user.role='admin' 但 /api/auth/permissions 返 is_platform_owner=false。
// 这种情况一般是后端 .env.prod 缺 PLATFORM_OWNER_USERNAME=<username> 或 DB 与 env 不匹配,
// 用户登录后会看到空白侧栏 + 所有模块 403,反复出现过几次,所以不再隐藏。
function PlatformOwnerMismatchBanner() {
  const { isPlatformOwnerMismatch, isLoading } = usePermissions()
  if (isLoading || !isPlatformOwnerMismatch) return null
  return (
    <div
      role="alert"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
        padding: '10px 16px',
        background: '#fff1f0', borderBottom: '2px solid #ff4d4f',
        color: '#a8071a', fontSize: 13, fontWeight: 500,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
      }}
    >
      <WarningOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
      <span>
        <strong>权限配置异常:</strong>
        当前账号是 admin 角色,但后端未识别为平台所有者,侧栏可能空白。
        请联系管理员检查后端 <code style={{ background: '#ffccc7', padding: '0 4px', borderRadius: 3 }}>PLATFORM_OWNER_USERNAME</code> 环境变量。
      </span>
    </div>
  )
}

function DefaultLandingRedirect({ projects, currentProjectId, isProjectsFetched }) {
  const { canViewModule, isPlatformOwner, isLoading: permLoading } = usePermissions()

  if (permLoading) {
    return <div style={{ padding: 40, textAlign: 'center' }}>加载中...</div>
  }

  let target = null
  for (const item of MENU_ITEMS) {
    // /platform-admin 仅 platform_owner 可见,普通用户跳过
    if (item.key === '/platform-admin') {
      if (isPlatformOwner) { target = item.key; break }
      continue
    }
    // 兜底:menu 项没填 module 时也允许(目前 MENU_ITEMS 只有 /platform-admin 满足)
    if (!item.module) { target = item.key; break }
    if (canViewModule(item.module)) { target = item.key; break }
  }
  // 无任何可访问模块时退到 /projects,RequireAnyModule 会显示「暂无可用模块」引导卡
  if (!target) target = '/projects'

  let finalPath = target
  if (!NO_PROJECT_KEYS.includes(target)) {
    if (!isProjectsFetched) {
      return <div style={{ padding: 40, textAlign: 'center' }}>加载中...</div>
    }
    const validProjectId = currentProjectId || (Array.isArray(projects) && projects.length > 0 ? projects[0].id : 0)
    finalPath = `${target}/${validProjectId}`
  }
  return <Navigate to={finalPath} replace />
}

// ============ 项目选择相关 Hooks ============
function useProjectSelection(projects, currentProjectId, setCurrentProjectId, navigate, location, queryClient) {
  // R-2026-08-08 (TDZ 修复):handleProjectChange 必须在 useEffect 之前定义,
  // 否则 useEffect 的依赖数组引用 handleProjectChange 触发 ReferenceError.
  // R-FIX-2026-08-16: 用 ref 跟踪 currentProjectId 最新值。handleProjectChange 触发
  // 'currentProjectIdChanged' 事件后,handleCustomChange 比较 ref 而非闭包旧值,避免事件自循环。
  // R-fix-2026-08-22:取 useAuth().user,handleProjectChange 写 lastVisited 时需要 user.id。
  // (ccc2a111 原始设计就有,后续我重新做时漏了 — ReferenceError: user is not defined 修法)
  const { user } = useAuth()
  const currentProjectIdRef = useRef(currentProjectId)
  useEffect(() => { currentProjectIdRef.current = currentProjectId }, [currentProjectId])

  const handleProjectChange = useCallback((projectId) => {
    setCurrentProjectId(projectId)
    // R-FIX-2026-08-16: 同步写 ref(必须在 dispatchEvent 之前)
    // useEffect 是 render 之后异步跑,事件 handler 同步触发时 ref 还是旧值 → 死循环
    currentProjectIdRef.current = projectId
    // R-sync-2026-10-21: 统一走 persistCurrentProject(全局 key + per-user key + 服务端),
    // 避免 ProjectList / PortfolioSection 各写一份导致行为不一致。
    persistCurrentProject(projectId, user?.id)
    // R145-Round3: 切项目清空 query cache,避免上个项目数据残留
    // (详情 queryKey 仅用 detailRecord.id,切项目后命中 cache 看到上项目数据)
    if (queryClient) queryClient.removeQueries()
    // 同 tab 内 localStorage 变化不会触发 'storage' 事件,显式 dispatch 让 AIAssistant 等页面同步
    window.dispatchEvent(new CustomEvent('currentProjectIdChanged', { detail: { projectId } }))
    // R-fix-2026-08-22: dropdown 切换项目时同步写 lastVisited.selectedProjectId,
    // 让 per-user key (pmwork_last_visited_${userId}) 持久化"上次选择的项目",
    // 刷新或下次登录后能从 lastVisited.selectedProjectId 恢复 currentProjectId。
    // ProjectGuard 写的是 lastVisited.projectId(基于 URL 访问),这里再补一行
    // selectedProjectId(基于 dropdown 切换),保证刷新时 ProjectsProvider 重排 + 项目兜底都准。
    const moduleMatch = location.pathname.match(/^\/(schedule|safety-quality|contract|investment|material|documents|project-map|dashboard|procedures|meeting|design|svo|qs|mr|risk|shared-data|site-management|project-health|concealed|energization|photo|co|site-contact|seal-usage|document-handover|work-handover|work-permit|document-check)\//)
    const currentModule = moduleMatch ? moduleMatch[1] : null
    // R-fix-2026-10-21:原来 `if (currentModule && user?.id)` — 在项目列表页(/projects)
    // 或根路径切项目时 currentModule 为 null → setLastVisited 不写 →
    // lastVisited.selectedProjectId 永远停在旧值,重登恢复到错的项目。
    // 改成:有 user.id 就写,currentModule 用现有值或 fallback 到 'dashboard'
    // (setLastVisited 内部对 module 为空会 return,这里保证非空)。
    if (user?.id) {
      setLastVisited(user.id, currentModule || 'dashboard', projectId, projectId)
    }
    const path = location.pathname
    if (path.includes('/site-management')) navigate(`/site-management/${projectId}`)
    else if (path.includes('/schedule')) navigate(`/schedule/${projectId}`)
    else if (path.includes('/safety-quality')) navigate(`/safety-quality/${projectId}`)
    else if (path.includes('/contract')) navigate(`/contract/${projectId}`)
    else if (path.includes('/investment')) navigate(`/investment/${projectId}`)
    else if (path.includes('/svo')) navigate(`/svo/${projectId}`)
    else if (path.includes('/qs')) navigate(`/qs/${projectId}`)
    else if (path.includes('/mr')) navigate(`/mr/${projectId}`)
    else if (path.includes('/risk')) navigate(`/risk/${projectId}`)
    else if (path.includes('/material')) navigate(`/material/${projectId}`)
    else if (path.includes('/documents')) navigate(`/documents/${projectId}`)
    else if (path.includes('/project-map')) navigate(`/project-map/${projectId}`)
    else if (path.includes('/procedures')) navigate(`/procedures/${projectId}`)
    else if (path.includes('/dashboard')) navigate(`/dashboard/${projectId}`)
    else if (path.includes('/shared-data')) navigate(`/shared-data/${projectId}`)
    else if (path.includes('/meeting')) navigate(`/meeting/${projectId}`)
    else if (path.includes('/concealed')) navigate(`/concealed/${projectId}`)
    else if (path.includes('/energization')) navigate(`/energization/${projectId}`)
    else if (path.includes('/photo')) navigate(`/photo/${projectId}`)
    else if (path.includes('/design')) navigate(`/design/${projectId}`)
    else if (path.includes('/project-health')) navigate(`/project-health/${projectId}`)
    // R239: AI 助理(/ai)、项目列表(/projects)等非项目路径不再强制跳 /dashboard,
    // setCurrentProjectId + localStorage + dispatch event 已足以让子页面同步项目切换
    // R-fix-2026-10-21: 加 user?.id — per-user key 写入需要它
  }, [location.pathname, navigate, setCurrentProjectId, queryClient, user?.id])

  // 处理项目初始选择
  // R-fix-2026-08-22: fallback 链 localStorage → lastVisited.selectedProjectId → projects[0]
  // 之前只兜底 projects[0](后端默认顺序),用户在 dropdown 切换的项目只在 lastVisited
  // 里有记录(projectId 字段),但 ProjectsProvider 拿 lastVisited.projectId 来重排
  // `projects[0]`,不写入 currentProjectId — 切换后立即刷新,top-right 显示顺序变了
  // 但页面跟着 navigate 又落到 projects[0],不一致。
  // 修法:同时把 lastVisited.selectedProjectId(由 handleProjectChange 写入)作为兜底源,
  // 优先级 projects[0] 之前。让 ProjectsProvider reorder + currentProjectId 选择都准。
  useEffect(() => {
    if (!Array.isArray(projects) || projects.length === 0) return
    if (!user?.id) return
    const validId = currentProjectId && projects.some(p => p.id === safeInt(currentProjectId))
    if (validId) return
    // R-fix-2026-10-21(per-user 隔离修复):恢复源优先级改为
    //   ① pmwork_current_project_${userId}(切项目时双写的 per-user key,最准)
    //   ② lastVisited.selectedProjectId(URL 访问兜底,可能不是"手动选的")
    //   ③ projects[0]
    // 之前只读 ②,但登出/login 清理会动到 pmwork_last_visited_*,且它的
    // selectedProjectId 只在 handleProjectChange 且 currentModule 命中模块路由时才写
    // (App.jsx:363 的 if (currentModule && user?.id) 条件) — 在项目列表页切项目时
    // currentModule 为 null → 不写 → ② 永远是旧值或 null → 恢复失败。
    let newId = null
    try {
      const perUser = localStorage.getItem(`pmwork_current_project_${user.id}`)
      if (perUser && /^\d+$/.test(perUser) && projects.some(p => p.id === Number(perUser))) {
        newId = Number(perUser)
      }
    } catch {}
    if (newId === null) {
      // R-sync-2026-10-21: ③ 服务端 current_project_id(换设备时本地无 key,靠它接上)
      // 放在 per-user 本地 key 之后 —— 同设备行为不变,只在本地没记录时才用,
      // 避免服务端同步失败时的旧值盖掉用户刚在本地做的选择。
      const serverPid = safeInt(user.current_project_id)
      if (serverPid && projects.some(p => p.id === serverPid)) {
        newId = serverPid
      }
    }
    if (newId === null) {
      const last = getLastVisited(user.id)
      const lastSelected = last?.selectedProjectId
      if (lastSelected && projects.some(p => p.id === safeInt(lastSelected))) {
        newId = safeInt(lastSelected)
      } else {
        newId = projects[0].id
      }
    }
    setCurrentProjectId(newId)
    localStorage.setItem('currentProjectId', String(newId))
    // 兜底选出的项目也写 per-user key,下次切账号回来能恢复
    try { localStorage.setItem(`pmwork_current_project_${user.id}`, String(newId)) } catch {}
  }, [user?.id, projects.length])

  // 跨标签页 / 同 tab 跨组件同步
  // R-2026-08-08 (audit P0-FE-5):之前只监听 'storage' 事件(跨浏览器 tab 触发),同 tab
  // 内 ProjectList 用 `dispatchEvent('currentProjectIdChanged')` 时 App.jsx 收不到,
  // 导致顶部项目选择器仍停在旧项目、下一次点菜单跳回旧项目。
  // 修法:同时监听 'storage'(跨 tab)+ 'currentProjectIdChanged'(同 tab),
  // 收到时复用 handleProjectChange 做完整流程(navigate + 清 cache)。
  useEffect(() => {
    const handleExternalChange = (projectId) => {
      // 用 ref 读最新值,避免 closure 旧值导致 currentProjectIdChanged 事件自循环
      if (projectId && String(projectId) !== String(currentProjectIdRef.current)) {
        handleProjectChange(projectId)
      }
    }
    const handleStorageChange = (e) => {
      if (e.key === 'currentProjectId' && e.newValue) handleExternalChange(e.newValue)
    }
    const handleCustomChange = (e) => handleExternalChange(e.detail?.projectId)
    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('currentProjectIdChanged', handleCustomChange)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('currentProjectIdChanged', handleCustomChange)
    }
  }, [currentProjectId, handleProjectChange])

  return { handleProjectChange }
}

// R-fix-2026-08-20 v5: 用户最终诉求,菜单按 module 权限显示,不再 hasAnyProject 过滤。
// 没项目的账号:左侧仍显示他有权访问的模块,点击后 URL /{id}=0 合法,页面显示极简 403 卡。

// ============ 侧边栏组件 ============
function AppSidebar({ collapsed, selectedKey, onMenuClick }) {
  // R-Audit-2026-08-04:isSuperAdmin 已不需要(canViewModule 后端处理 super_admin
  // 自动继承 org.features),只留 isPlatformOwner
  const { canViewModule, isPlatformOwner } = usePermissions()

  const items = useMemo(() => {
    if (isPlatformOwner) {
      // R-audit-2026-08-30 P2-004: MENU_ITEMS 已移除 /platform-admin(原本 MENU_ITEMS
      // 里有这一项,但非 platform-owner 分支会 filter 掉,platform-owner 分支会包含,
      // 形成「重复 menu 项」语义不一致),这里显式 push 给 platform-owner 用
      return [
        ...MENU_ITEMS,
        // R-audit-2026-08-30 P2-007:加 module: 'platform_owner' 让 sidebar 自描述
        { key: '/platform-admin', label: '平台管理', icon: <CloudOutlined />, module: 'platform_owner' },
      ]
    }
    return [
      ...MENU_ITEMS.filter(item => {
        // /admin 单独判断:org super_admin/admin 都要看 canViewModule('admin')
        // (super_admin 继承 org.features,admin 由超管显式授权)
        if (item.key === '/admin') return canViewModule('admin')
        // R-audit-2026-08-30 P2-007:platform_owner module 走 isPlatformOwner
        if (item.module === 'platform_owner') return isPlatformOwner
        if (!item.module) return true
        return canViewModule(item.module)
      }),
    ]
  }, [isPlatformOwner, canViewModule])

  return (
    <aside
      className={`app-sidebar${collapsed ? ' app-sidebar--collapsed' : ''}`}
    >
      <div className="app-sidebar-header">
        <div className="app-sidebar-logo">
          <span>EP</span>
        </div>
        <span className="app-sidebar-title">项目管理</span>
      </div>
      <div className="app-sidebar-divider" />
      <Menu mode="inline" selectedKeys={[selectedKey]} onClick={onMenuClick}
        className="scrollbar-hidden"
        inlineCollapsed={collapsed}
        // R-fix-2026-09-11 v3: 用 _internalDisableMenuItemTitleTooltip 关 antd Tooltip
        // 只保留 1 个原生浏览器 title tooltip(<a title="...">)
        // - antd Menu 5.14 在 title 是字符串时,默认同时生成:
        //     a) <a title="..."> 浏览器原生 tooltip — 立即出现,小灰底
        //     b) antd <Tooltip> 包裹整个 <li> — 大白底,有 mouseEnterDelay
        //   两者叠加 = 用户看到"先出现一个小的,然后再出现一个大的"
        // - 设 _internalDisableMenuItemTitleTooltip:true 后,antd Tooltip 不包裹,
        //   只剩 <a title="...">,鼠标 hover 整个 li 行(因为 <a> 占 li 全部空间)
        //   都触发这一个原生 tooltip,无叠加
        _internalDisableMenuItemTitleTooltip={collapsed}
        items={items.map((it, idx, arr) => {
          // R-audit-2026-08-30 P2-004:divider 自身无 module,filter 永远保留 → member 只开通 ai/审批时 sidebar 底部残留分隔线
          // 改:若 divider 后面没有非 divider 项,渲染 null(隐藏)
          if (it.type === 'divider') {
            const hasItemAfter = arr.slice(idx + 1).some(x => x.type !== 'divider')
            if (!hasItemAfter) return null
            return { ...it, label: null }
          }
          if (collapsed) {
            return {
              ...it,
              icon: it.icon,
              label: it.icon,
              title: it.label,  // → antd 渲染为 <a title={it.label}>
            }
          }
          return {
            ...it,
            label: <span className="app-sidebar-label">{it.label}</span>,
          }
        })}
        style={{ height: 'calc(100% - var(--sidebar-header-height))', maxHeight: 'calc(100% - var(--sidebar-header-height))', borderRight: 0, overflowY: 'auto', overflowX: 'hidden', touchAction: 'pan-y', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', minHeight: 0 }} />
    </aside>
  )
}

// ============ 头部组件 ============
function AppHeader({ collapsed, onCollapse, currentProjectId, projects, onProjectChange, showProjectSelector, user, onUserInfoClick, isFullscreen, onToggleFullscreen, onOpenSearch }) {
  // R-fix-2026-08-02: 显式 destructure isPlatformOwner,平台所有者跳过项目过滤
  // (canAccessProject 内部对 isPlatformOwner 已经短路 true,但 readability + debug 友好)
  const { canAccessProject, isPlatformOwner } = usePermissions()

  // 过滤出用户有权访问的项目
  // R-Audit-2026-08-04 #E 第三轮:不用全局 isSuperAdmin 短路(可能跨组织误判),
  // canAccessProject 现在用项目级字段,统一走 filter。
  const accessibleProjects = useMemo(() => {
    if (isPlatformOwner) return projects
    return projects.filter(p => canAccessProject(p.id))
  }, [projects, isPlatformOwner, canAccessProject])

  // 左上角标题自动显示登录组织名称;平台所有者/未加入组织(name=None)回退平台名
  const { data: myOrg } = useApiQuery(
    ['my-org', user?.id],
    '/api/organizations/me/org',
    undefined,
    { enabled: !!user?.id, staleTime: STALE_TIME.LONG },
  )
  const platformName = myOrg?.name || '新能源项目信息平台'

  return (
    <Header className="app-header-bar">
      <div className="app-header-title">
        <Tooltip title={collapsed ? '展开侧边栏' : '折叠侧边栏'}>
          <MenuFoldOutlined onClick={onCollapse} style={{
            fontSize: 18, cursor: 'pointer', marginRight: 12, color: 'var(--brand-navy)',
            transform: collapsed ? 'rotate(180deg)' : 'none', transition: 'transform var(--sidebar-anim-duration) var(--sidebar-anim-easing)'
          }} />
        </Tooltip>
        <span className="app-header-platform-name" style={{ fontSize: 16, fontWeight: 600, color: 'var(--brand-navy)', whiteSpace: 'nowrap' }}>{platformName}</span>
      </div>
      <div className="app-header-right">
        {showProjectSelector && accessibleProjects.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {/* R-feat-2026-10-14 #5 易用性:项目收藏选择器(★ 在下拉 + 当前项目右侧) */}
            <ProjectFavoriteSelect
              projects={accessibleProjects}
              currentProjectId={currentProjectId}
              onProjectChange={onProjectChange}
            />
          </div>
        )}
        {/* R-audit-2026-10-07 P1:NotificationBell 抛错 → silent fallback,其他组件仍可用 */}
        <ErrorBoundary silentFallback>
          <Suspense fallback={null}>
            <NotificationBell />
          </Suspense>
        </ErrorBoundary>
        <Tooltip title="全局搜索 (Ctrl+K)">
          <Button
            aria-label="全局搜索 (Ctrl+K)" type="text"
            icon={<SearchOutlined style={{ fontSize: 18 }} />}
            onClick={onOpenSearch}
            style={{ display: 'flex', alignItems: 'center' }}
            aria-label="全局搜索"
          />
        </Tooltip>
        <Tooltip title={isFullscreen ? '退出全屏(Esc)' : '进入全屏'}>
          <Button
            type="text"
            icon={isFullscreen ? <CompressOutlined style={{ fontSize: 18 }} /> : <ExpandOutlined style={{ fontSize: 18 }} />}
            onClick={onToggleFullscreen}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          />
        </Tooltip>
        <UserDropdown user={user} onUserInfoClick={onUserInfoClick} />
      </div>
    </Header>
  )
}

// ============ 用户下拉菜单 ============
// R-fix-2026-08-20 v3: 去掉 effective role tag (commit 87a63d43 加的),
// 用户反馈: 右上角显示两个 (头像 chip + role tag) 太冗余.
// 保留原版只有一个头像 chip, 角色信息在"账户信息"弹窗里看.
function UserDropdown({ user, onUserInfoClick }) {
  const { logout } = useAuth()
  const navigate = useNavigate()

  const handleMenuClick = async ({ key }) => {
    if (key === 'logout') {
      await logout()
      navigate('/login', { replace: true })
    } else if (key === 'user-info') {
      onUserInfoClick()
    }
  }

  // CJK 字符串按码点切，避免半个字符
  const displayName = Array.from(user?.full_name || user?.username || 'U').slice(0, 3).join('')

  return (
    <Dropdown menu={{
      items: [
        { key: 'user-info', label: '账户信息' },
        { type: 'divider' },
        { key: 'logout', label: '退出登录', danger: true }
      ],
      onClick: handleMenuClick
    }} trigger={['click']}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 10px', height: 28, borderRadius: 6,
        background: 'linear-gradient(135deg, #e6f4ff 0%, #bae0ff 100%)',
        color: 'var(--brand-navy)', fontSize: 12, fontWeight: 500,
        whiteSpace: 'nowrap'
      }}>
        {displayName}
      </span>
    </Dropdown>
  )
}

// ============ 用户信息弹窗 ============
// R260: 已抽出到 src/components/UserInfoModal.jsx + lazy import(见顶部),首屏不再加载
// ============ AppContent 主组件 ============
function AppContent() {
  const { user, loading } = useAuth()
  const projects = useProjects()
  const isProjectsFetched = useProjectsFetched()
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsMobile()  // F2-12:H5 自适应 — 移动端自动隐藏侧栏
  const [currentProjectId, setCurrentProjectId] = useState(() => localStorage.getItem('currentProjectId') || null)
  // 注意:本组件的 queryClient 在下方才声明(AppContent 尾部),这里不能用变量,
  // 直接调 hook 拿实例(React hook 顺序固定,放最前面安全)。
  const cpidQueryClient = useQueryClient()
  // R-fix-2026-10-21(per-user 隔离最终修正):
  // AppContent 挂在路由里不随登录 remount,A 账号的 currentProjectId(state)会活到 B 登录。
  // 下面 line 689 的校验只查「是否在可见项目里」— A/B 常有共同项目(如都参与项目 1),
  // 校验通过 → currentProjectId 保持 A 的值 → B 登录看到 A 的项目。
  //
  // 修:user.id 一旦确定,强制从 per-user key 重建 currentProjectId,
  // 不管当前 state 里是什么。这个 effect 必须跑在所有兜底逻辑之前。
  const appliedUserForCpidRef = useRef(null)
  useEffect(() => {
    const uid = user?.id
    if (!uid) return
    if (appliedUserForCpidRef.current === uid) return   // 该用户已应用过,跳过
    appliedUserForCpidRef.current = uid
    let perUser = null
    try {
      perUser = localStorage.getItem(`pmwork_current_project_${uid}`)
    } catch {}
    if (perUser && /^\d+$/.test(perUser)) {
      // 该账号有历史选择 → 无条件覆盖(A 的残留值在此被清掉)
      if (String(currentProjectId) !== String(perUser)) {
        setCurrentProjectId(perUser)
        try { localStorage.setItem('currentProjectId', perUser) } catch {}
        if (cpidQueryClient) cpidQueryClient.removeQueries()
        window.dispatchEvent(new CustomEvent('currentProjectIdChanged', { detail: { projectId: perUser } }))
      }
    } else {
      // 该账号无历史选择 → 清掉(可能来自别的账号),让兜底 effect 走 projects[0]
      if (currentProjectId) {
        setCurrentProjectId(null)
        try { localStorage.removeItem('currentProjectId') } catch {}
      }
    }
  }, [user?.id])
  // R-2026-09-22 (P1-4 数据隔离加固,xiaoxie 事件衍生):user 变化时校验 localStorage 的
  // currentProjectId 是否仍属于新用户的可见项目 — 不同账号在同一浏览器切换时,
  // 即便有 shouldPreserveKey 白名单的兜底,currentProjectId 仍可能残留导致用户被路由到越权项目。
  // isProjectsFetched=true 时检查;用户明确选过的项目可保留(无需每次都重置)。
  //
  // ⚠️ 第一版只在 user.id 变化时 fire(用 ref 比较),刷新页面时 user.id 不变 → useEffect
  // 不跑 → 残留 currentProjectId 漏过去。改成「user.id 变化 OR 首次拿到 projects」都触发校验。
  const prevUserIdForCpidRef = useRef(null)
  useEffect(() => {
    const userChanged = prevUserIdForCpidRef.current !== user?.id
    if (userChanged) prevUserIdForCpidRef.current = user?.id
    if (!currentProjectId || !isProjectsFetched) return
    if (!Array.isArray(projects) || !projects.some(p => p.id === safeInt(currentProjectId))) {
      localStorage.removeItem('currentProjectId')
      setCurrentProjectId(null)
    }
  }, [user?.id, currentProjectId, isProjectsFetched, projects])
  const [userInfoModalOpen, setUserInfoModalOpen] = useState(false)
  // R-feat-2026-09-05: 新手引导 — 首次登录(per-user localStorage)或 ?onboarding=1 重看
  const [onboardingOpen, setOnboardingOpen] = useState(false)
  useEffect(() => {
    if (!user?.id) return
    try {
      const params = new URLSearchParams(location.search)
      if (params.get('onboarding') === '1') { setOnboardingOpen(true); return }
      if (!localStorage.getItem(`pmwork_onboarding_done_${user.id}`)) setOnboardingOpen(true)
    } catch {}
  }, [user?.id, location.search])
  const closeOnboarding = (dontShow = true) => {
    setOnboardingOpen(false)
    // R-fix-2026-09-06(用户需求): 勾选「下次不再显示」才落标记; 不勾则下次登录再弹
    try { if (user?.id && dontShow) localStorage.setItem(`pmwork_onboarding_done_${user.id}`, '1') } catch {}
  }

  // R-fix-2026-08-20:用户切换时(AppContent 不 remount)重置 currentProjectId + localStorage,
  // 防止上一个账号的 projectId 残留。AppContent 不会因为 user 改变而 remount,所以 React state
  // 保留前账号的值;若前账号选了项目 116,新账号点 /design 会 navigate('/design/116') →
  // 后端 403(无访问权)+ useApiQuery throwOnError:false 静默失败 → 渲染空数据,
  // 用户看到的是"设计管理页面没数据,sidebar 设计管理不高亮"。
  //
  // R-fix-2026-08-22:跳过首次 mount 的 null → user.id 触发。
  // 之前 useRef 初始值是 `user?.id ?? null` = null(user 还在 fetch),useEffect 在 user
  // 加载回来时把 ref 从 null → user.id 当作"账号变了",清掉 currentProjectId + localStorage。
  // 结果:用户刷新页面 → 顶栏项目名立刻被清掉,useEffect [projects.length] 兜底又写回
  // projects[0](后端默认顺序),与用户上次选的对不上。
  // 修法:仅当 prev 非 null 且 currentUid 非 null 且两者不同时,才算账号切换。
  const prevUserIdRef = useRef(user?.id ?? null)
  useEffect(() => {
    const currentUid = user?.id ?? null
    const prev = prevUserIdRef.current
    // 跳过首次加载(null → user.id),只处理"两个已登录账号切换"的场景
    if (prev !== null && currentUid !== null && prev !== currentUid) {
      prevUserIdRef.current = currentUid
      // R-fix-2026-10-21(per-user 隔离):原来直接 removeItem('currentProjectId') 清空,
      // 让新账号走 projects[0] — 导致 A/B 账号登录看到同一个项目(全局单值污染)。
      // 改成:从新账号自己的 per-user key 恢复上次选择,恢复不到才清空走默认。
      let restored = null
      try {
        restored = localStorage.getItem(`pmwork_current_project_${currentUid}`)
      } catch {}
      if (restored && /^\d+$/.test(restored)) {
        setCurrentProjectId(restored)
        try { localStorage.setItem('currentProjectId', restored) } catch {}
      } else {
        setCurrentProjectId(null)
        try { localStorage.removeItem('currentProjectId') } catch {}
      }
    } else {
      prevUserIdRef.current = currentUid
    }
  }, [user?.id])

  // R-perf-2026-08-30: 空闲预取项目总览/数据看板两个高频页面的懒加载 chunk
  // (页面 chunk + echarts ~236KB gzip)。这两页首访在弱网要等数秒下载解析,
  // 登录后浏览器空闲时预下载进缓存,首次导航即秒开。
  // 省流模式(navigator.connection.saveData)下跳过,不偷跑流量。
  useEffect(() => {
    if (!user?.id) return
    const ric = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 2000))
    ric(() => {
      if (navigator.connection?.saveData) return
      import('./pages/ProjectList').catch(() => {})
      import('./pages/Dashboard').catch(() => {})
      import('./components/common/charts/EChartsLazy').catch(() => {})
    })
  }, [user?.id])
  const buildInfo = useBuildInfo()
  // 首次访问默认折叠(节省屏幕宽度);用户主动展开后,localStorage 记 'false' 覆盖
  // R-2026-09-21: 按 userId 隔离 localStorage,跨账号不继承
  const [collapsed, setCollapsed] = useUserLocalStorage('sidebarCollapsed', true)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [sidebarHovered, setSidebarHovered] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  // 2026-10-17 易用性 U3: 快捷键面板 Modal
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  // chord 状态:按了 g 后记录时间戳,1s 内按第二段才生效
  // chordRef.current = { prefix: 'g', at: 1700000000000 }
  const chordRef = useRef(null)
  const queryClient = useQueryClient()

  useDraggableModal()

  // chord 超时清理:每次进入 g 等待态启动 1s 超时,超时后清空 prefix
  const startChordTimeout = useCallback(() => {
    const at = Date.now()
    chordRef.current = { prefix: 'g', at }
    setTimeout(() => {
      if (chordRef.current && chordRef.current.at === at) {
        chordRef.current = null
      }
    }, 1000)
  }, [])

  // 通用导航跳转:有 currentProjectId 用项目页,无则用 /projects
  const navigateWithProject = useCallback((routeBase, fallbackRoute = '/projects') => {
    const pid = currentProjectId || (projects.length > 0 ? projects[0]?.id : 0)
    if (pid) navigate(`/${routeBase}/${pid}`)
    else navigate(fallbackRoute)
  }, [currentProjectId, projects, navigate])

  // chord helper:若当前是 g 等待态就消费并返回 true
  const consumeChord = useCallback(() => chordRef.current?.prefix === 'g', [])

  // 2026-10-17 易用性 U3: 全局快捷键(替代旧 useEffect 单一 Ctrl+K)
  //   - Cmd/Ctrl+K 打开全局搜索
  //   - ? 打开快捷键面板(Shift+/ 触发)
  //   - g s / g d / g w 两段组合导航(进度管理 / 数据看板 / 我的工作)
  useShortcuts([
    {
      key: 'k', require: 'cmd', handler: () => setSearchOpen(true),
      description: '打开全局搜索', category: '搜索',
    },
    {
      key: '?', handler: () => setShortcutsOpen(true),
      description: '显示快捷键面板', category: '帮助',
    },
    {
      key: 'g', handler: () => { startChordTimeout() },
      description: '组合前缀(g 后 1s 内按字母)', category: '导航',
    },
    {
      key: 's', handler: () => {
        if (consumeChord()) { chordRef.current = null; navigateWithProject('schedule') }
      },
      description: '跳转进度管理(g s)', category: '导航',
    },
    {
      key: 'd', handler: () => {
        if (consumeChord()) { chordRef.current = null; navigateWithProject('dashboard') }
      },
      description: '跳转数据看板(g d)', category: '导航',
    },
    {
      key: 'w', handler: () => {
        if (consumeChord()) { chordRef.current = null; navigate('/workbench') }
      },
      description: '跳转我的工作(g w)', category: '导航',
    },
    {
      key: 'Escape', allowInEditable: true, handler: () => {
        if (shortcutsOpen) setShortcutsOpen(false)
        else if (searchOpen) setSearchOpen(false)
      },
      description: '关闭当前弹窗', category: '弹窗',
    },
  ])

  // 全屏:鼠标靠近左边缘 20px 时浮出侧栏,移出侧栏区域(>260px 或离开侧栏)时收回
  useEffect(() => {
    if (!isFullscreen) {
      setSidebarHovered(false)
      return
    }
    const handleMove = (e) => {
      const x = e.clientX
      if (x <= 20) {
        // 靠近左边缘,展开
        setSidebarHovered(true)
      } else if (x > 260) {
        // 远离侧栏 260px 以外,收回
        setSidebarHovered(false)
      }
      // 20 < x <= 260 保持当前状态(避免在侧栏上移动时反复切换)
    }
    document.addEventListener('mousemove', handleMove)
    return () => document.removeEventListener('mousemove', handleMove)
  }, [isFullscreen])

  // 监听系统全屏状态(用户按 Esc 或浏览器主动退出时同步 React state)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFs = !!document.fullscreenElement
      setIsFullscreen(isFs)
      if (!isFs) setSidebarHovered(false)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  // 项目列表已在 AppInner 中通过 ProjectsProvider 提供，无需重复获取

  const { handleProjectChange } = useProjectSelection(projects, currentProjectId, setCurrentProjectId, navigate, location, queryClient)

  // URL项目ID验证 - match 结果 memo 化，避免 effect 每次 render 跑
  // R2-P0-1: 旧正则漏 svo/qs/mr/risk/shared-data/site-management/project-health/
  //          concealed/energization/photo/co 等 :projectId 路由,
  //          导致 isProjectPage=false,攻击者直接访问 /svo/9999 跳过 urlProjectId 校验
  //          (虽然后端 verify_project_access 会拦 403,但前端探测窗口仍是 P0)
  const projectPathMatch = useMemo(
    () => location.pathname.match(/^\/(schedule|safety-quality|contract|material|documents|project-map|dashboard|procedures|meeting|design|svo|qs|mr|risk|shared-data|site-management|project-health|concealed|energization|photo|co|site-contact|seal-usage|document-handover|work-handover|work-permit|document-check)\/(\d+)/),
    [location.pathname]
  )
  const isProjectPage = !!projectPathMatch
  const projectKey = projectPathMatch ? projectPathMatch[1] : ''
  const urlProjectId = safeInt(projectPathMatch?.[2])

  // projectId 无效时重定向(最多 1 次,避免 react-query refetch 反复触发 history 污染)
  const navigatedRef = useRef(false)
  useEffect(() => {
    if (navigatedRef.current) return
    if (!isProjectPage || !isProjectsFetched || !Array.isArray(projects) || projects.length === 0) return
    const currentId = safeInt(currentProjectId)
    const currentIdInList = currentId && projects.some(p => p.id === currentId)
    const urlIdInList = !isNaN(urlProjectId) && projects.some(p => p.id === urlProjectId)

    if (isNaN(urlProjectId) || !urlIdInList) {
      navigatedRef.current = true
      const target = currentIdInList ? currentId : projects[0].id
      navigate(`/${projectKey}/${target}`, { replace: true })
    }
  }, [isProjectPage, isProjectsFetched, projectKey, urlProjectId, currentProjectId, projects, navigate])

  // R-fix-2026-08-20 v2: 登录后跳 / 让 DefaultLandingRedirect 接管
  //
  // 用户需求:
  //   1. 第一个页面 = 左侧导航栏第一个模块 (MENU_ITEMS 顺序)
  //   2. 项目 ID = 下拉框第一个项目 (projects[0].id)
  //
  // DefaultLandingRedirect 已实现这两个逻辑 (走 MENU_ITEMS + projects[0].id),
  // 我们只需 navigate('/') 让它跑. 避免重复造轮子 + 与侧边栏菜单顺序一致
  // (修 R5-C 报的 P2 obs: MODULE_ORDER 顺序与 MENU_ITEMS 不一致, 导致
  // 登录跳 /dashboard/0 但刷新 / 跳 /projects 的 UX 不一致).
  //
  // 历史: B 任务 (commit b45f2dd8) 写过 3 段优先级 (lastVisited → MODULE_ORDER → /),
  // 但与侧边栏菜单顺序不一致, 现在统一走 DefaultLandingRedirect.
  // lastVisited 写入仍保留 (ProjectGuard 自动写), 读取不再需要 (URL 持久化).
  useEffect(() => {
    if (loading || !user || location.pathname !== '/login') return
    navigate('/', { replace: true })
  }, [user, loading, location.pathname, navigate])

if (loading) {
  return <div style={{ padding: 40, textAlign: 'center' }}>加载中...</div>
}

  // R-fix-2026-07-29: URL 是 /login 直接渲染 Login(跳过整个 Layout/Sidebar/Header),
  // 避免 stale user state 导致双布局叠加(左侧导航栏 + 右侧登录框)
  // R-fix-2026-09-20: Login 是 lazy 组件,裸 return 在 sync 更新中 chunk 未加载即 suspend
  // 且无 Suspense 边界 → #426(同 MobileTabBar,见下方 Content 内注释)
  if (location.pathname === '/login') {
    return (
      <Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>加载中...</div>}>
        <Login />
      </Suspense>
    )
  }

  // R243-G: 把 !user 提到 isProjectsFetched 之前
  // 之前 user=null(429/401 后 fetchUser catch 设的) + isProjectPage=true → 永远卡"加载中"
  // 因为 AppInner 的 useQuery({ enabled: !!user }) 因 enabled:false 永不 fetch,
  // isProjectsFetched 恒 false,加载判断永远命中
  // R-fix-2026-09-05 审计 P0: 访客分享页必须在 !user 拦截之前渲染,
  // 否则无痕访客打开 /share/report/x 看到的是登录页,分享功能完全不可用
  if (location.pathname.startsWith('/share/')) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>加载中...</div>}>
          <ShareReportPage />
        </Suspense>
      </ErrorBoundary>
    )
  }

  // R-audit-2026-09-20-r3 P0-1:Suspense 内层加 ErrorBoundary,防止 React.lazy() chunk 加载失败
  // (CDN 挂 / chunk hash 不一致 / 老 tab)导致整个 App 白屏。
  // 内层 catch 后显示「请刷新页面」+ reload,保留外层侧栏。
  // 2026-10-04: lazyErrorFallback 之前被 LazyErrorBoundary 复用(也删除),现无调用方。
  // 删了避免 eslint no-unused-vars warning + 占行。需要时再恢复(Alert + reload 按钮的标准兜底)。

  if (!user) {
    // 2026-10-03 R-audit: Login 是 lazy 组件,裸 return 在 sync 更新中 chunk 未加载即 suspend,
    // 会抛 "A component suspended while responding to synchronous input",ErrorBoundary 兜底全屏提示。
    // 用 Suspense 包裹(已有 L813-820 模式可复用)
    return (
      <Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>加载中...</div>}>
        <Login />
      </Suspense>
    )
  }

  if (isProjectPage && !isProjectsFetched) {
    return <div style={{ padding: 40, textAlign: 'center' }}>加载中...</div>
  }

  const selectedKey = getSelectedKey(location.pathname)
  // 2026-09-01 OPT-A3: 删 'location.pathname !== \'/approvals\'' — 该路径已不存在
  const showProjectSelector = location.pathname !== '/projects' && location.pathname !== '/test' && location.pathname !== '/admin' && !location.pathname.includes('/platform-admin') && location.pathname !== '/workbench'

  // 菜单点击
  const handleMenuClick = ({ key }) => {
    // 2026-09-01 OPT-A3: 删 '/approvals' — 不再有该路径
    // 2026-09-12 独立化:'/agent-portal' 不再是内部路由,而是跳转 /api/agent/launch
    // 服务端会读 PM cookie → 签 JWT → 302 到独立子域 agent.pmwork.site
    // 2026-09-14 解耦:WindDocAgent 完全从 PM 项目移除(独立项目 ~/projects/wind-doc-agent/),
    // 侧栏入口已删除,本 click handler 死代码清理。
    if (key === '/projects' || key === '/ai' || key === '/test' || key === '/shared-data' || key === '/admin' || key === '/platform-admin' || key === '/workbench') {
      navigate(key)
      return
    }
    const validProjectId = currentProjectId || (projects.length > 0 ? projects[0].id : 0)
    navigate(`${key}/${validProjectId}`)
  }

  const sidebarVisible = (!isFullscreen || sidebarHovered) && !isMobile  // F2-12:移动端隐藏侧栏
  const sidebarWidth = isMobile ? 0 : (collapsed ? 60 : 140)  // F2-12:移动端 sidebarWidth=0
  // 全屏模式:侧栏悬浮,内容区不被压缩(始终 0 margin)
  // 正常模式:侧栏占位,内容区让出空间
  const contentMarginLeft = (isFullscreen || isMobile) ? 0 : sidebarWidth  // F2-12:移动端 content 占满

  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(err => {
        if (import.meta.env.DEV) console.warn('requestFullscreen failed:', err)
      })
    } else {
      document.exitFullscreen?.()
    }
  }

  return (
    <>
      {/* R-fix-2026-10-19 R16-P2-1:skip-nav 跳转链接 — 键盘 / 屏幕阅读器用户 Tab 第一下可达 */}
      <a href="#main-content" className="skip-nav">跳到主要内容</a>
      <Layout style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden' }}>
      {/* R-feat-2026-10-17 U4:断网 banner + 离线队列提示(顶部 sticky)
          必须在 PlatformOwnerMismatchBanner 之后(权限错配优先级更高) */}
      <NetworkStatusBanner />
      {/* R-audit-2026-08-26: 检测到「DB role=admin 但 is_platform_owner=false」不一致时,显示醒目红条横幅
          用户硬性要求「侧栏空白以后避免再出现」,不再静默 */}
      <PlatformOwnerMismatchBanner />
      <div
        style={{
          width: isFullscreen ? (sidebarHovered ? sidebarWidth : 0) : sidebarWidth,
          opacity: sidebarVisible ? 1 : 0,
          transition: 'width var(--sidebar-anim-duration) var(--sidebar-anim-easing), opacity var(--sidebar-anim-duration) var(--sidebar-anim-easing)',
          position: 'fixed', left: 0, top: 0, bottom: 0,
          // R-fix-2026-09-15: 100 → 300 让侧栏在项目组合 expanded Card(z-index:100)之上,不被遮住
          zIndex: 300,
          boxShadow: isFullscreen && sidebarHovered ? '2px 0 8px rgba(0,0,0,0.15)' : 'none',
          pointerEvents: sidebarVisible ? 'auto' : 'none'
        }}
        onMouseEnter={() => isFullscreen && setSidebarHovered(true)}
        onMouseLeave={() => isFullscreen && setSidebarHovered(false)}
      >
        <AppSidebar collapsed={collapsed} selectedKey={selectedKey} onMenuClick={handleMenuClick} />
      </div>
      <Layout style={{ marginLeft: contentMarginLeft, transition: 'margin-left var(--sidebar-anim-duration) var(--sidebar-anim-easing)', display: 'flex', flexDirection: 'column', minHeight: '100%', height: '100%' }}>
        <AppHeader
          collapsed={collapsed}
          onCollapse={() => setCollapsed(!collapsed)}
          currentProjectId={currentProjectId}
          projects={projects}
          onProjectChange={handleProjectChange}
          showProjectSelector={showProjectSelector}
          user={user}
          onUserInfoClick={() => setUserInfoModalOpen(true)}
          isFullscreen={isFullscreen}
          onToggleFullscreen={handleToggleFullscreen}
          onOpenSearch={() => setSearchOpen(true)}
        />
        <Content id="main-content" tabIndex={-1} className="app-page-content">
          {/* R-2026-08-22:项目权限拒绝常驻 banner(内联在 Content 顶部,贴第一行卡片上方)。
              紧凑样式:Alert banner,单行展示缺失的模块名,避免占满屏幕。 */}
          <ProjectPermissionBanner />
          {/* F2-12:移动端底部 tabBar(仅 isMobile=true 时显示) */}
          {/* R-fix-2026-09-20: MobileTabBar 是 lazy 组件,在 Suspense 之外裸渲染 →
              登录跳转等 sync 更新中 chunk 未加载即 suspend 且无边界 → React 抛
              "A component suspended while responding to synchronous input"(#426)
              → ErrorBoundary 全屏兜底。移入独立 Suspense(fallback=null,tabBar
              未加载时本就不显示;lazy resolve 后不会再 suspend)。 */}
          <ErrorBoundary silentFallback>
            <Suspense fallback={null}>
              <MobileTabBar isMobile={isMobile} />
            </Suspense>
          </ErrorBoundary>
          <Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>加载中...</div>}>
            <OnboardingModal open={onboardingOpen} onClose={closeOnboarding} />
            <ProjectNotFoundListener />
          <Routes>
              {/* /login 路由:axios 401 拦截器跳 /login?expired=1 时,user 可能还有 stale 值,
                  这里需要单独匹配到 Login 组件,否则会掉到 path="*" → 404 */}
              {/* R-audit-2026-10-07 P1:/login 加 ErrorBoundary,Login chunk 加载失败(CDN 挂 / chunk hash 不一致 / 网络断开)全局兜底 */}
              <Route path="/login" element={<ErrorBoundary><Login /></ErrorBoundary>} />
              {/* R-feat-2026-09-05: 访客只读报告分享页(免登录,必须在鉴权 guard 之外) */}
              <Route path="/share/report/:token" element={<ErrorBoundary><ShareReportPage /></ErrorBoundary>} />
              <Route path="/" element={<DefaultLandingRedirect projects={projects} currentProjectId={currentProjectId ? safeInt(currentProjectId) : null} isProjectsFetched={isProjectsFetched} />} />
              <Route path="/dashboard/:projectId" element={<ErrorBoundary><RequireModule module="dashboard"><ProjectGuard module="dashboard"><Dashboard /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/projects" element={<ErrorBoundary><RequireModule module="projects"><ProjectList /></RequireModule></ErrorBoundary>} />
              {/* R-audit-2026-09-20:项目组合放大独立页 — 无侧栏(layout 走 hideSidebar 路径) */}
              <Route path="/projects/portfolio" element={<ErrorBoundary><RequireModule module="projects"><ProjectsPortfolio /></RequireModule></ErrorBoundary>} />
              <Route path="/schedule/:projectId" element={<ErrorBoundary><RequireModule module="schedule"><ProjectGuard module="schedule"><Schedule /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/safety-quality/:projectId" element={<ErrorBoundary><RequireModule module="safety_quality"><ProjectGuard module="safety-quality"><SafetyQuality /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/contract/:projectId" element={<ErrorBoundary><RequireModule module="contract"><ProjectGuard module="contract"><Contract /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/investment" element={<ErrorBoundary><RequireModule module="investment"><Investment /></RequireModule></ErrorBoundary>} />
              <Route path="/investment/:projectId" element={<ErrorBoundary><RequireModule module="investment"><ProjectGuard module="investment"><InvestmentDetail /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/svo/:projectId" element={<ErrorBoundary><RequireModule module="svo"><ProjectGuard module="svo"><Svo /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/site-contact/:projectId" element={<ErrorBoundary><RequireModule module="site_contact"><ProjectGuard module="site-contact"><SiteContact /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/seal-usage/:projectId" element={<ErrorBoundary><RequireModule module="seal_usage"><ProjectGuard module="seal-usage"><SealUsage /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/document-handover/:projectId" element={<ErrorBoundary><RequireModule module="document_handover"><ProjectGuard module="document-handover"><DocumentHandover /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/work-handover/:projectId" element={<ErrorBoundary><RequireModule module="work_handover"><ProjectGuard module="work-handover"><WorkHandover /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/work-permit/:projectId" element={<ErrorBoundary><RequireModule module="work_permit"><ProjectGuard module="work-permit"><WorkPermit /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/qs/:projectId" element={<ErrorBoundary><RequireModule module="qs"><ProjectGuard module="qs"><Qs /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/mr/:projectId" element={<ErrorBoundary><RequireModule module="material"><ProjectGuard module="material"><Mr /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/risk/:projectId" element={<ErrorBoundary><RequireModule module="risk"><ProjectGuard module="risk"><RiskPage /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/material/:projectId" element={<ErrorBoundary><RequireModule module="material"><ProjectGuard module="material"><Material /></ProjectGuard></RequireModule></ErrorBoundary>} />
              {/* R-fix-2026-09-05 方案 A: 所有附件预览统一走 /preview/:type/:id
                  - documents / regulations: type={documents|regulations} id={record.id}
                  - procedures / contracts / training: type=... id=parent_id,
                    ?attachment=N(因为这些模块附件是 JSON 数组,无独立 id)
                  - 模块资料 ModuleMaterials: /api/documents/{id}/file → /preview/documents/{id}
                  旧 /preview/attachment?file_url=... 路由已删除(7 步走完) */}
              {/* R-audit-2026-09-20 P0-3:加 RequireAuth 守卫,未登录用户不能直接 hit /preview/:type/:id */}
              <Route path="/preview/:type/:id" element={<ErrorBoundary><RequireAuth><FilePreview /></RequireAuth></ErrorBoundary>} />
              {/* R-fix-2026-09-05: 兼容旧路径 /documents/preview/:id(转 documents 类型) */}
              <Route path="/documents/preview/:id" element={<Navigate to="/preview/documents/:id" replace />} />
              <Route path="/documents/:projectId" element={<ErrorBoundary><RequireModule module="documents"><ProjectGuard module="documents"><Documents /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/project-map/:projectId" element={<ErrorBoundary><RequireModule module="project-map"><ProjectGuard module="project-map"><ProjectMap /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/procedures/:projectId" element={<ErrorBoundary><RequireModule module="procedures"><ProjectGuard module="procedures"><Procedures /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/shared-data/:projectId" element={<ErrorBoundary><RequireModule module="persons"><ProjectGuard module="shared-data"><SharedData /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/shared-data" element={<ErrorBoundary><RequireModule module="persons"><SharedData /></RequireModule></ErrorBoundary>} />
              <Route path="/site-management/:projectId" element={<ErrorBoundary><RequireModule module="svo"><ProjectGuard module="site-management"><SiteManagement /></ProjectGuard></RequireModule></ErrorBoundary>} />
              {/* R-audit-2026-08-30 P2-006: /site-management bare-path fallback(SiteManagement 嵌套子页,无 bare 时落到 * NotFoundPage) */}
              <Route path="/site-management" element={<Navigate to="/site-management/0" replace />} />
              <Route path="/project-health/:projectId" element={<ErrorBoundary><RequireModule module="dashboard"><ProjectGuard module="project-health"><ProjectHealth /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/meeting" element={<Navigate to="/projects" replace />} />
              <Route path="/meeting/:projectId" element={<ErrorBoundary><RequireModule module="meeting"><ProjectGuard module="meeting"><Meeting /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/concealed/:projectId" element={<ErrorBoundary><RequireModule module="concealed"><ProjectGuard module="concealed"><Concealed /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/energization/:projectId" element={<ErrorBoundary><RequireModule module="energization"><ProjectGuard module="energization"><Energization /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/photo/:projectId" element={<ErrorBoundary><RequireModule module="photo"><ProjectGuard module="photo"><Photo /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/design/:projectId" element={<ErrorBoundary><RequireModule module="design"><ProjectGuard module="design"><Design /></ProjectGuard></RequireModule></ErrorBoundary>} />
              <Route path="/design/" element={<Navigate to="/projects" replace />} />
              <Route path="/ai" element={<ErrorBoundary><RequireModule module="ai"><AIAssistant /></RequireModule></ErrorBoundary>} />
              {/* 2026-09-14 WindDocAgent 解耦:WindDocAgent 已是独立项目,本页死代码清理 */}
              {/* 2026-09-19 F1-G04 + F2-04 UI 补完: 恢复 /approvals 独立页 + 详情页
                  (原 /approvals → /workbench fallback 已移除,见 getSelectedKey) */}
              <Route path="/approvals" element={<ErrorBoundary><RequireModule module="my-workbench"><MyApprovals /></RequireModule></ErrorBoundary>} />
              <Route path="/approvals/:id" element={<ErrorBoundary><RequireModule module="my-workbench"><ApprovalInstanceDetail /></RequireModule></ErrorBoundary>} />
              {/* R-2026-09-19: 项目组合已合并到投资控制(/investment/:id)第 7 个 tab,删除独立路由 */}
              {/* R-fix-2026-09-19: /portfolio/* 兜底重定向到投资控制页(旧 bookmark 不再 404)*/}
              <Route path="/portfolio/*" element={<Navigate to="/investment" replace />} />
              {/* 2026-09-19 F2-08 OpenAPI 文档页(admin 模块) */}
              <Route path="/admin/api-docs" element={<ErrorBoundary><RequireModule module="admin"><OpenApiDocs /></RequireModule></ErrorBoundary>} />
              {/* M4-A: 工作负载页 */}
              <Route path="/workload" element={<ErrorBoundary><RequireModule module="dashboard"><Workload /></RequireModule></ErrorBoundary>} />
              {/* M4-B: 工时填报 */}
              <Route path="/timesheet" element={<ErrorBoundary><RequireModule module="dashboard"><Timesheet /></RequireModule></ErrorBoundary>} />
              {/* M4-C: 资源池(平台级,仅 platform_owner / super_admin) */}
              <Route path="/resource-pool" element={<ErrorBoundary><RequireModule module="admin"><ResourcePool /></RequireModule></ErrorBoundary>} />
              {/* M4-D: 2FA 设置 */}
              <Route path="/security/2fa" element={<ErrorBoundary><RequireModule module="my-workbench"><TwoFactorSettings /></RequireModule></ErrorBoundary>} />
              {/* M5-B: S-Curve 进度投资联动 */}
              <Route path="/s-curve" element={<ErrorBoundary><RequireModule module="investment"><SCurve /></RequireModule></ErrorBoundary>} />
              {/* M5-C: 自定义仪表盘 */}
              <Route path="/dashboard-customize" element={<ErrorBoundary><RequireModule module="dashboard"><DashboardCustomize /></RequireModule></ErrorBoundary>} />
              {/* R-audit-2026-09-20:健康看板 / OnPrem / 审计日志 已迁到 PlatformAdmin,
                  旧路由重定向避免收藏链接 404(只 platform_owner 能跳,组织 admin 会撞 require_admin) */}
              <Route path="/admin/health-dashboard" element={<RequireModule module="admin"><Navigate to="/platform-admin" replace /></RequireModule>} />
              <Route path="/admin/onprem" element={<RequireModule module="admin"><Navigate to="/platform-admin" replace /></RequireModule>} />
              <Route path="/admin/audit-log" element={<RequireModule module="admin"><Navigate to="/platform-admin" replace /></RequireModule>} />
              {/* M6-E: 报表模板编辑器 */}
              <Route path="/admin/report-templates" element={<ErrorBoundary><RequireModule module="admin"><ReportTemplateEditor /></RequireModule></ErrorBoundary>} />
              <Route path="/admin" element={<ErrorBoundary><RequireModule module="admin"><Admin /></RequireModule></ErrorBoundary>} />
              <Route path="/platform-admin" element={<ErrorBoundary><PlatformOwnerOnly><RequireModule module="admin"><PlatformAdmin /></RequireModule></PlatformOwnerOnly></ErrorBoundary>} />
              {/* 2026-08-31 个人工作台 — 项目无关页,不包 ProjectGuard */}
              <Route path="/workbench" element={<ErrorBoundary><RequireModule module="my-workbench"><MyWorkbench /></RequireModule></ErrorBoundary>} />
              {/* 2026-08-31 日历(产品差距分析第 4 项):跨项目事件聚合,dashboard 权限即可访问 */}
              <Route path="/calendar" element={<ErrorBoundary><RequireModule module="dashboard"><Calendar /></RequireModule></ErrorBoundary>} />
              {/* 2026-09-01 个人工作台 P1.5 — 模板管理(项目无关,用 dashboard module 作权限门槛) */}
              <Route path="/work-logs/templates" element={<ErrorBoundary><RequireModule module="dashboard"><WorkLogTemplates /></RequireModule></ErrorBoundary>} />
              {/* 2026-09-01 P2.2 — 日志列表 + 编辑(嵌入工作台同时也独立页) */}
              <Route path="/work-logs" element={<ErrorBoundary><RequireModule module="dashboard"><WorkLogs /></RequireModule></ErrorBoundary>} />
              {/* 2026-09-01 报表系统重构 P6.3 — 模板化生成 + 多期对比 + 多格式导出 */}
              <Route path="/reports" element={<ErrorBoundary><RequireModule module="dashboard"><Reports /></RequireModule></ErrorBoundary>} />
              {/* 制度规范库 — 全员可查阅(登录即可),不经 RequireModule(非模块权限,后端自行鉴权) */}
              <Route path="/regulations" element={<ErrorBoundary><Regulations /></ErrorBoundary>} />
              {/* 资料检查 v2 — 挂在 documents 模块下(自动随 documents 授权),需登录 */}
              <Route path="/document-check" element={<Navigate to="/projects" replace />} />
              <Route path="/document-check/:projectId" element={<ErrorBoundary><RequireModule module="document_check"><ProjectGuard module="document_check"><DocumentCheck /></ProjectGuard></RequireModule></ErrorBoundary>} />
              {/* AI 检查依据管理(从 AI 检查 modal「管理依据」按钮进入,无需 projectId) */}
              <Route path="/document-check-criteria" element={<ErrorBoundary><RequireModule module="document_check"><DocumentCheckCriteria /></RequireModule></ErrorBoundary>} />
              <Route path="/approval-designer" element={<Navigate to="/admin" replace />} />
              <Route path="/test" element={<ErrorBoundary><PlatformOwnerOnly><RequireModule module="admin"><TestData /></RequireModule></PlatformOwnerOnly></ErrorBoundary>} />
              <Route path="/schedule/" element={<Navigate to="/projects" replace />} />
              <Route path="/safety-quality/" element={<Navigate to="/projects" replace />} />
              <Route path="/contract/" element={<Navigate to="/projects" replace />} />
              <Route path="/material/" element={<Navigate to="/projects" replace />} />
              <Route path="/documents/" element={<Navigate to="/projects" replace />} />
              <Route path="/construction-log/:projectId" element={<Navigate to="/meeting/:projectId" replace />} />
              <Route path="/construction-log/" element={<Navigate to="/meeting" replace />} />
              <Route path="/project-map/" element={<Navigate to="/projects" replace />} />
              <Route path="/dashboard/" element={<Navigate to="/projects" replace />} />
              <Route path="/meeting/" element={<Navigate to="/projects" replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </Content>
        {/* R260: UserInfoModal + GlobalSearchModal 都是 lazy 组件,需要独立 Suspense 兜住
            否则首次打开时 lazy chunk 还在加载,React 会 throw "A component suspended while
            responding to synchronous input" → 整个 App 崩。它们都用 Modal 包装,显示 fallback
            不会暴露内部加载状态 (Modal 没 open 时 DOM 不可见,fallback 几乎不会被看到) */}
        {/* R-audit-2026-10-07 P1:UserInfoModal/GlobalSearchModal 抛错 → silent fallback,其他组件仍可用 */}
        <ErrorBoundary silentFallback>
          <Suspense fallback={null}>
            <UserInfoModal open={userInfoModalOpen} onClose={() => setUserInfoModalOpen(false)} user={user} />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary silentFallback>
          <Suspense fallback={null}>
            <GlobalSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
          </Suspense>
        </ErrorBoundary>
        {/* 2026-10-17 易用性 U3: 快捷键面板 — 按 ? 打开 */}
        <ErrorBoundary silentFallback>
          <Suspense fallback={null}>
            <ShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} shortcuts={GLOBAL_SHORTCUTS} />
          </Suspense>
        </ErrorBoundary>
        {/* R-fix-2026-08-02: 右下角小字显示当前 bundle 的 commit + 构建时间。
            排障"修过的问题复发"时一眼能看出浏览器是否还在缓存旧 bundle。 */}
        <div
          data-build-version={buildInfo.commit}
          style={{
            position: 'fixed', bottom: 4, right: 8, fontSize: 10, color: '#bbb',
            fontFamily: 'monospace', pointerEvents: 'none', userSelect: 'none', zIndex: 1,
          }}
          title={`构建 ${buildInfo.build_time} · 分支 ${buildInfo.branch}`}
        >
          {buildInfo.commit} · {buildInfo.build_time.replace('T', ' ').replace('Z', '')}
        </div>
      </Layout>
    </Layout>
    </>
  )
}

// ============ App 入口 ============
export default function App() {
  return (
    <AntApp>
      <AuthProvider>
        <AppInner />
      </AuthProvider>
    </AntApp>
  )
}

function AppInner() {
  const { user } = useAuth()
  // /api/schedule/ 返回项目列表(含 project_count)用于顶部项目选择器,
  // 内部实现为 schedule router 的 GET / 因为项目数据内聚在工程进度模块里
  // R-audit-2026-08-27-R2: useQuery → useApiQuery 让 module 检查 + 401 retry 拦截生效
  // D4.5:queryKey 走 qk.projects()(与其他项目列表页 prefix 同步,失效更可靠)
  const { data, isFetched } = useApiQuery(
    qk.projects(),
    '/api/schedule/',
    undefined,
    { enabled: !!user, module: 'projects', staleTime: STALE_TIME.MEDIUM },
  )
  const projects = data?.items || []

  return (
    <ProjectsProvider projects={projects} isFetched={isFetched}>
      <AppContent />
    </ProjectsProvider>
  )
}